import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, humanise, mobile as fmtMobile } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, Textarea, useToast } from '../components/ui';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  admins: number;
}

interface Catalogue {
  actions: string[];
  areas: Array<{ key: string; label: string; actions: string[] }>;
}

interface AdminRow {
  id: string;
  displayName: string | null;
  mobile: string;
  email: string | null;
  isBlocked: boolean;
  isSuperAdmin: boolean;
  lastLoginAt: string | null;
  adminRole: { id: string; name: string } | null;
}

interface Draft {
  id: string | null;
  name: string;
  description: string;
  permissions: Set<string>;
}

/** How many permissions a role has, said the way a person would. */
function summary(role: Role): string {
  if (role.permissions.includes('*')) return 'Everything';
  return `${role.permissions.length} permission${role.permissions.length === 1 ? '' : 's'}`;
}

export function RolesPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [viewing, setViewing] = useState<Role | null>(null);

  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: () => api.get<Role[]>('/admin/roles') });
  const catalogue = useQuery({ queryKey: ['admin-permissions'], queryFn: () => api.get<Catalogue>('/admin/permissions') });
  const admins = useQuery({ queryKey: ['admin-admins'], queryFn: () => api.get<AdminRow[]>('/admin/admins') });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-roles'] });
    qc.invalidateQueries({ queryKey: ['admin-admins'] });
  };

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { name: d.name.trim(), description: d.description.trim(), permissions: [...d.permissions] };
      return d.id ? api.patch(`/admin/roles/${d.id}`, body) : api.post('/admin/roles', body);
    },
    onSuccess: () => {
      toast('Role saved. It applies to the next thing each admin with it does.', 'success');
      setDraft(null);
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/roles/${id}`),
    onSuccess: () => {
      toast('Role deleted', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const assign = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      api.put(`/admin/users/${userId}/admin-role`, { roleId }),
    onSuccess: () => {
      toast('Role changed', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (roles.isError) {
    return (
      <Empty
        icon="⛔"
        title="Roles are managed by a super admin"
        body={(roles.error as Error).message}
      />
    );
  }

  const toggle = (permission: string) =>
    setDraft((d) => {
      if (!d) return d;
      const next = new Set(d.permissions);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return { ...d, permissions: next };
    });

  const all = draft?.permissions.has('*') ?? false;

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      <Notice tone="info" title="What a role allows is checked by the server on every request">
        Hiding a menu item is a courtesy; the API is what refuses. A change to a role, or to who holds it, applies to the
        next thing that admin does, with no need to sign out. A super admin is never restricted by a role, and an admin
        with no role has full access.
      </Notice>

      <Card flush>
        <CardHead
          title="Roles"
          sub="Built-in roles can be assigned but not changed"
          action={
            <Button
              size="sm"
              variant="primary"
              onClick={() => setDraft({ id: null, name: '', description: '', permissions: new Set() })}
            >
              New role
            </Button>
          }
        />
        {roles.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 160 }} />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Allows</th>
                  <th>Admins</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(roles.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="cell-strong">
                        {r.name}{' '}
                        {r.isSystem && (
                          <span style={{ marginLeft: 6 }}>
                            <Badge tone="neutral">Built in</Badge>
                          </span>
                        )}
                      </div>
                      <div className="cell-muted">{r.description ?? ''}</div>
                    </td>
                    <td>{summary(r)}</td>
                    <td className="cell-strong">{r.admins}</td>
                    <td>
                      <div className="cell-actions">
                        <Button size="sm" onClick={() => setViewing(r)}>
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            setDraft({
                              id: r.isSystem ? null : r.id,
                              name: r.isSystem ? `${r.name} (copy)` : r.name,
                              description: r.description ?? '',
                              permissions: new Set(r.permissions),
                            })
                          }
                        >
                          {r.isSystem ? 'Copy' : 'Edit'}
                        </Button>
                        {!r.isSystem && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={r.admins > 0}
                            loading={remove.isPending && remove.variables === r.id}
                            onClick={() => remove.mutate(r.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card flush>
        <CardHead title="Admins" sub="Who can sign in to this console, and what each may do" />
        {admins.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 120 }} />
          </div>
        ) : !admins.data?.length ? (
          <Empty icon="☖" title="No admins" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Last signed in</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {admins.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="cell-strong">
                        <Link to={`/users/${a.id}`}>{a.displayName ?? fmtMobile(a.mobile)}</Link>
                        {a.isBlocked && (
                          <span style={{ marginLeft: 7 }}>
                            <Badge tone="red">Blocked</Badge>
                          </span>
                        )}
                      </div>
                      <div className="cell-muted">{a.email ?? fmtMobile(a.mobile)}</div>
                    </td>
                    <td className="cell-muted">{a.lastLoginAt ? date(a.lastLoginAt) : 'never'}</td>
                    <td>
                      {a.isSuperAdmin ? (
                        <Badge tone="purple">Super admin · everything</Badge>
                      ) : (
                        <Select
                          value={a.adminRole?.id ?? ''}
                          placeholder="No role · full access"
                          onChange={(e) => assign.mutate({ userId: a.id, roleId: e.target.value || null })}
                          options={(roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ margin: 0, padding: 'var(--s4) var(--s5)', fontSize: 13, color: 'var(--ink-muted)' }}>
          To make somebody an admin, open their page under Users and grant the Admin role. Only a super admin can.
        </p>
      </Card>

      {viewing && (
        <Modal title={viewing.name} sub={viewing.description ?? undefined} onClose={() => setViewing(null)} wide>
          {viewing.permissions.includes('*') ? (
            <p style={{ margin: 0 }}>Everything in the console.</p>
          ) : (
            <div className="stack">
              {(catalogue.data?.areas ?? [])
                .map((area) => ({
                  area,
                  held: viewing.permissions.filter((p) => p.startsWith(`${area.key}.`)).map((p) => p.split('.')[1]),
                }))
                .filter((x) => x.held.length > 0)
                .map(({ area, held }) => (
                  <div key={area.key} className="row" style={{ justifyContent: 'space-between', gap: 'var(--s4)' }}>
                    <span className="cell-strong">{area.label}</span>
                    <span className="row" style={{ gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {held.map((h) => (
                        <Badge key={h} tone={h === 'manage' ? 'purple' : 'blue'}>
                          {h === 'manage' ? 'Everything here' : humanise(h)}
                        </Badge>
                      ))}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </Modal>
      )}

      {draft && (
        <Modal
          title={draft.id ? 'Edit role' : 'New role'}
          sub="Tick what admins with this role may do."
          onClose={() => setDraft(null)}
          wide
          footer={
            <>
              <Button onClick={() => setDraft(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={draft.name.trim().length < 2 || draft.permissions.size === 0}
                onClick={() => save.mutate(draft)}
              >
                Save role
              </Button>
            </>
          }
        >
          <div className="stack">
            <Field label="Name">
              <Input value={draft.name} autoFocus onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="What it is for" hint="Optional. Shown in the list of roles.">
              <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </Field>

            <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={all}
                onChange={() => setDraft({ ...draft, permissions: all ? new Set() : new Set(['*']) })}
              />
              <span className="cell-strong">Everything in the console</span>
            </label>

            {!all && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Area</th>
                      {(catalogue.data?.actions ?? []).map((a) => (
                        <th key={a} style={{ textAlign: 'center' }}>
                          {a === 'manage' ? 'All' : humanise(a)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(catalogue.data?.areas ?? []).map((area) => {
                      const manages = draft.permissions.has(`${area.key}.manage`);
                      return (
                        <tr key={area.key}>
                          <td className="cell-strong">{area.label}</td>
                          {(catalogue.data?.actions ?? []).map((action) => {
                            const permission = `${area.key}.${action}`;
                            // Not every area has every action: there is no
                            // "approve" for a setting.
                            if (!area.actions.includes(action)) return <td key={action} />;
                            return (
                              <td key={action} style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  aria-label={`${area.label}: ${action}`}
                                  // "All" covers the rest, so they show as
                                  // ticked and cannot be unticked beside it.
                                  checked={draft.permissions.has(permission) || (manages && action !== 'manage')}
                                  disabled={manages && action !== 'manage'}
                                  onChange={() => toggle(permission)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
